import React, { useContext, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
/// Image
import profile from "../../../images/avatar/profile-1.jpg";
import { Dropdown } from "react-bootstrap";
import Select from "react-select";
import LogoutPage from './Logout';
import { ThemeContext } from "../../../context/ThemeContext";
import { Fn_FillListData } from "../../../store/Functions";
import { API_WEB_URLS } from "../../../constants/constAPI";
import { checkRegistrationExpiry } from "../../utils/RegistrationExpiryHelper";
import { isNotInUse, filterActiveMasters } from "../../utils/masterFilters";
import { MONTH_NAMES, getPreviousMonthAndYear } from "../../utils/useSession";

const currentYear = new Date().getFullYear();
const baseYearList = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const notifyStorage = () => {
  window.dispatchEvent(new Event("storage"));
  window.dispatchEvent(new Event("sessionChange"));
};

const Header = ({ onNote }) => {
  const dispatch = useDispatch();
  const { background, changeBackground } = useContext(ThemeContext);
  const isDark = background?.value === "dark";

  // Default Previous Month & Year (e.g. October 2026 -> September 2026)
  const defaultPeriod = getPreviousMonthAndYear();

  // Session Selector States
  const [firmList, setFirmList] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selectedFirm") || "null"); } catch { return null; }
  });
  const [branchList, setBranchList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selectedBranch") || "null"); } catch { return null; }
  });

  // When software opens: auto-select previous month & year if not already set or in fresh browser session
  // When software opens: auto-select saved or previous month & year if not already set
  const [selectedMonth, setSelectedMonth] = useState(() => {
    sessionStorage.setItem("softwareSessionActive", "true");
    const saved = localStorage.getItem("selectedPayrollMonth");
    if (!saved || !saved.trim()) {
      localStorage.setItem("selectedPayrollMonth", defaultPeriod.month);
      return defaultPeriod.month;
    }
    return saved.trim();
  });

  const [selectedYear, setSelectedYear] = useState(() => {
    sessionStorage.setItem("softwareSessionActive", "true");
    const saved = localStorage.getItem("selectedPayrollYear");
    if (!saved || !saved.trim()) {
      localStorage.setItem("selectedPayrollYear", defaultPeriod.year);
      return defaultPeriod.year;
    }
    return saved.trim();
  });

  const YEAR_LIST = selectedYear && !baseYearList.includes(Number(selectedYear))
    ? [Number(selectedYear), ...baseYearList].sort((a, b) => a - b)
    : baseYearList;

  // Sync state from localStorage
  const syncFromStorage = useCallback(() => {
    try {
      const sf = JSON.parse(localStorage.getItem("selectedFirm") || "null");
      setSelectedFirm(sf);
    } catch {
      setSelectedFirm(null);
    }
    try {
      const sb = JSON.parse(localStorage.getItem("selectedBranch") || "null");
      setSelectedBranch(sb);
    } catch {
      setSelectedBranch(null);
    }
    const def = getPreviousMonthAndYear();
    setSelectedMonth(localStorage.getItem("selectedPayrollMonth") || def.month);
    setSelectedYear(localStorage.getItem("selectedPayrollYear") || def.year);
  }, []);

  // Listen to storage and sessionChange events
  useEffect(() => {
    window.addEventListener("storage", syncFromStorage);
    window.addEventListener("sessionChange", syncFromStorage);
    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener("sessionChange", syncFromStorage);
    };
  }, [syncFromStorage]);

  // Load firm list on mount
  useEffect(() => {
    const apiURL = API_WEB_URLS.MASTER + "/0/token/" + API_WEB_URLS.FirmMaster + "/Id/0";
    Fn_FillListData(dispatch, (data) => {
      const list = data || [];
      setFirmList(list);
      const saved = localStorage.getItem("selectedFirm");
      let parsed = null;
      try { parsed = JSON.parse(saved); } catch {}
      if ((!parsed || !parsed.Id) && list.length > 0) {
        const firstFirm = list[0];
        setSelectedFirm(firstFirm);
        localStorage.setItem("selectedFirm", JSON.stringify(firstFirm));
        notifyStorage();
      }
    }, "gridData", apiURL);
  }, [dispatch]);

  // Load branches when selectedFirm changes
  useEffect(() => {
    if (!selectedFirm || !selectedFirm.Id) {
      setBranchList([]);
      setSelectedBranch(null);
      return;
    }
    const branchApi = API_WEB_URLS.MASTER + "/0/token/GetBranchByFirmId/Id/" + selectedFirm.Id;
    Fn_FillListData(dispatch, setBranchList, "gridData", branchApi).then((dataList) => {
      if (dataList && dataList.length > 0) {
        const saved = localStorage.getItem("selectedBranch");
        let validSavedBranch = null;
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            validSavedBranch = dataList.find(b => String(b.Id) === String(parsed.Id));
          } catch {}
        }

        if (validSavedBranch) {
          setSelectedBranch(validSavedBranch);
        } else if (dataList.length === 1) {
          const singleBranch = dataList[0];
          setSelectedBranch(singleBranch);
          localStorage.setItem("selectedBranch", JSON.stringify(singleBranch));
          notifyStorage();
        } else {
          setSelectedBranch(null);
          localStorage.removeItem("selectedBranch");
          notifyStorage();
        }
      } else {
        setBranchList([]);
        setSelectedBranch(null);
        localStorage.removeItem("selectedBranch");
        notifyStorage();
      }
    }).catch(() => {
      setBranchList([]);
      setSelectedBranch(null);
      localStorage.removeItem("selectedBranch");
      notifyStorage();
    });
  }, [selectedFirm?.Id, dispatch]);

  const handleFirmChange = (selectedOption) => {
    if (!selectedOption || (!selectedOption.value && !selectedOption.target?.value)) {
      setSelectedFirm(null);
      setSelectedBranch(null);
      setBranchList([]);
      localStorage.removeItem("selectedFirm");
      localStorage.removeItem("selectedBranch");
      notifyStorage();
      return;
    }
    const val = selectedOption.value !== undefined ? selectedOption.value : selectedOption.target?.value;
    const firm = selectedOption.raw || firmList.find((f) => String(f.Id) === String(val));
    if (firm) {
      setSelectedFirm(firm);
      localStorage.setItem("selectedFirm", JSON.stringify(firm));
      localStorage.removeItem("selectedBranch");
      notifyStorage();
    }
  };

  const handleBranchChange = (e) => {
    const bid = e.target.value;
    if (!bid) {
      setSelectedBranch(null);
      localStorage.removeItem("selectedBranch");
      notifyStorage();
      return;
    }
    const branch = branchList.find((b) => String(b.Id) === String(bid));
    if (branch) {
      setSelectedBranch(branch);
      localStorage.setItem("selectedBranch", JSON.stringify(branch));
      notifyStorage();
    }
  };

  // On mount: ensure storage listeners are triggered so all components sync initial month/year
  useEffect(() => {
    notifyStorage();
  }, []);

  const handleMonthChange = (e) => {
    const val = e.target.value;
    setSelectedMonth(val);
    if (val) {
      localStorage.setItem("selectedPayrollMonth", val);
    } else {
      localStorage.removeItem("selectedPayrollMonth");
    }
    sessionStorage.setItem("softwareSessionActive", "true");
    notifyStorage();
  };

  const handleYearChange = (e) => {
    const val = e.target.value;
    setSelectedYear(val);
    if (val) {
      localStorage.setItem("selectedPayrollYear", val);
    } else {
      localStorage.removeItem("selectedPayrollYear");
    }
    sessionStorage.setItem("softwareSessionActive", "true");
    notifyStorage();
  };

  // Check Registration Expiry for currently selected branch
  const expiryAlert = checkRegistrationExpiry(selectedBranch);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
  
  // Function to get role name based on F_UserType
  const getRoleName = (userType) => {
    switch (userType) {
      case 1:
        return "Admin";
      case 2:
        return "Supervisor";
      case 3:
        return "Operator";
      default:
        return "User";
    }
  };

  const toggleTheme = () => {
    const nextThemeValue = background.value === 'light' ? 'dark' : 'light';
    changeBackground({ value: nextThemeValue, label: nextThemeValue === 'dark' ? 'Dark' : 'Light' });
  };

  // Filter and format firms for searchable Select
  const activeFirms = filterActiveMasters(firmList, selectedFirm?.Id);
  const firmOptions = activeFirms.map((f) => ({
    value: f.Id,
    label: f.Name,
    raw: f,
  }));
  const selectedFirmOption = selectedFirm
    ? { value: selectedFirm.Id, label: selectedFirm.Name, raw: selectedFirm }
    : null;

  const firmSelectStyles = {
    container: (base) => ({
      ...base,
      minWidth: "110px",
      maxWidth: "200px",
      flexShrink: 1,
    }),
    control: (base) => ({
      ...base,
      minHeight: "26px",
      height: "26px",
      background: "transparent",
      border: "none",
      boxShadow: "none",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: selectedFirm ? 600 : 400,
      padding: 0,
      "&:hover": {
        border: "none",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 2px",
      height: "26px",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? "#f8fafc" : "#1e293b",
      fontSize: "12px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      margin: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? "#94a3b8" : "#64748b",
      fontSize: "12px",
      whiteSpace: "nowrap",
      margin: 0,
    }),
    input: (base) => ({
      ...base,
      color: isDark ? "#f8fafc" : "#1e293b",
      margin: 0,
      padding: 0,
      fontSize: "12px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "26px",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 2px",
      color: isDark ? "#94a3b8" : "#64748b",
      "&:hover": {
        color: isDark ? "#f8fafc" : "#1e293b",
      },
      svg: {
        width: "14px",
        height: "14px",
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "0 2px",
      color: isDark ? "#94a3b8" : "#64748b",
      "&:hover": {
        color: "#ef4444",
      },
      svg: {
        width: "14px",
        height: "14px",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 999999,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 999999,
      background: isDark ? "#1e2638" : "#ffffff",
      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      borderRadius: "8px",
      minWidth: "220px",
      marginTop: "4px",
      overflow: "hidden",
    }),
    menuList: (base) => ({
      ...base,
      padding: "4px",
      maxHeight: "220px",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      borderRadius: "6px",
      margin: "1px 0",
      padding: "6px 10px",
      fontSize: "12.5px",
      cursor: "pointer",
      background: isSelected
        ? "#3b82f6"
        : isFocused
        ? isDark
          ? "#2e3a52"
          : "#eff6ff"
        : "transparent",
      color: isSelected
        ? "#ffffff"
        : isFocused
        ? isDark
          ? "#ffffff"
          : "#1d4ed8"
        : isDark
        ? "#cbd5e1"
        : "#1e293b",
      fontWeight: isSelected ? 600 : 400,
      "&:active": {
        background: "#3b82f6",
        color: "#ffffff",
      },
    }),
  };

  return (
    <div className="header">
      <div className="header-content">
        <nav className="navbar navbar-expand p-0 h-100">
          <div className="navbar-3part">

            {/* ═══════════════════════════════════════════════════════════
                PART 1: LEFT (Width = Sidebar Width = 200px)
                Executive User Identity / Role Micro-Card
                ═══════════════════════════════════════════════════════════ */}
            <div className="navbar-part-left d-none d-lg-flex">
              <div 
                className="d-flex align-items-center w-100 px-2 py-1"
                style={{
                  background: isDark ? 'rgba(30, 41, 59, 0.7)' : '#f8fafc',
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  height: '32px',
                  overflow: 'hidden'
                }}
                title={`${getRoleName(userData?.userType)} : ${userData?.name || 'User'}`}
              >
                {/* Avatar with online status */}
                <div className="position-relative me-2 flex-shrink-0" style={{ width: '22px', height: '22px' }}>
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center text-white"
                    style={{
                      width: '22px',
                      height: '22px',
                      background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
                      fontSize: '11px',
                      fontWeight: 700
                    }}
                  >
                    {(userData?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span 
                    className="position-absolute rounded-circle"
                    style={{
                      width: '6px',
                      height: '6px',
                      background: '#10b981',
                      border: '1px solid #ffffff',
                      bottom: '-1px',
                      right: '-1px'
                    }}
                  />
                </div>

                {/* Details */}
                <div className="d-flex flex-column justify-content-center" style={{ minWidth: 0, flex: 1 }}>
                  <div 
                    className="fw-bold" 
                    style={{ 
                      fontSize: '11.5px', 
                      color: isDark ? '#f8fafc' : '#1e293b',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {userData?.name || 'User'}
                  </div>
                  <span 
                    style={{ 
                      fontSize: '9.5px', 
                      fontWeight: 600,
                      color: isDark ? '#93c5fd' : '#2563eb',
                      lineHeight: 1
                    }}
                  >
                    {getRoleName(userData?.userType)}
                  </span>
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════
                PART 2: CENTER (Flex 1)
                Unified Workspace Session Command Capsule
                ═══════════════════════════════════════════════════════════ */}
            <div className="navbar-part-center">
              <div className="session-capsule">
                {/* Firm Selector */}
                <div className="d-flex align-items-center flex-shrink-0" title="Select Active Firm">
                  <i className="fa fa-building text-primary me-1" style={{ fontSize: '12px' }} />
                  <Select
                    isSearchable
                    isClearable
                    placeholder="-- Select Firm --"
                    options={firmOptions}
                    value={selectedFirmOption}
                    onChange={handleFirmChange}
                    styles={firmSelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPlacement="auto"
                  />
                </div>

                {/* Divider */}
                <div style={{ width: '1px', height: '16px', background: isDark ? '#334155' : '#cbd5e1', margin: '0 6px' }} />

                {/* Branch Selector */}
                {branchList.length > 1 && (
                  <>
                    <div className="d-flex align-items-center flex-shrink-0" title="Select Active Branch">
                      <i className="fa fa-code-fork text-info me-1" style={{ fontSize: '12px' }} />
                      <select
                        className="form-select form-select-sm border-0 bg-transparent"
                        style={{ 
                          fontSize: '11.5px', 
                          fontWeight: selectedBranch ? 600 : 400,
                          color: isDark ? '#f8fafc' : '#1e293b', 
                          cursor: 'pointer',
                          maxWidth: '120px',
                          padding: '0 18px 0 0',
                          outline: 'none',
                          boxShadow: 'none',
                          height: '24px'
                        }}
                        value={selectedBranch ? selectedBranch.Id : ""}
                        onChange={handleBranchChange}
                      >
                        <option value="" style={{ color: '#000' }}>-- Branch --</option>
                        {filterActiveMasters(branchList, selectedBranch?.Id).map((branch) => (
                          <option key={branch.Id} value={branch.Id} style={{ color: '#000' }}>{branch.Name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ width: '1px', height: '16px', background: isDark ? '#334155' : '#cbd5e1', margin: '0 6px' }} />
                  </>
                )}

                {branchList.length === 1 && selectedBranch && (
                  <>
                    <span 
                      className="badge d-flex align-items-center px-2 py-1 flex-shrink-0"
                      style={{ 
                        background: isDark ? 'rgba(6, 182, 212, 0.15)' : '#e0f2fe',
                        color: isDark ? '#38bdf8' : '#0284c7',
                        border: `1px solid ${isDark ? '#0284c7' : '#bae6fd'}`,
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        height: '22px'
                      }}
                      title={`Branch: ${selectedBranch.Name}`}
                    >
                      <i className="fa fa-code-fork me-1" />
                      {selectedBranch.Name}
                    </span>
                    <div style={{ width: '1px', height: '16px', background: isDark ? '#334155' : '#cbd5e1', margin: '0 6px' }} />
                  </>
                )}

                {/* Month Selector */}
                <div className="d-flex align-items-center flex-shrink-0" title="Select Payroll Month">
                  <i className="fa fa-calendar text-warning me-1" style={{ fontSize: '12px' }} />
                  <select
                    className="form-select form-select-sm border-0 bg-transparent"
                    style={{ 
                      fontSize: '11.5px', 
                      fontWeight: selectedMonth ? 600 : 400,
                      color: isDark ? '#f8fafc' : '#1e293b', 
                      cursor: 'pointer',
                      maxWidth: '105px',
                      padding: '0 18px 0 0',
                      outline: 'none',
                      boxShadow: 'none',
                      height: '24px'
                    }}
                    value={selectedMonth}
                    onChange={handleMonthChange}
                  >
                    <option value="" style={{ color: '#000' }}>-- Month --</option>
                    {MONTH_NAMES.map((m) => (
                      <option key={m} value={m} style={{ color: '#000' }}>{m}</option>
                    ))}
                  </select>
                </div>

                {/* Divider */}
                <div style={{ width: '1px', height: '16px', background: isDark ? '#334155' : '#cbd5e1', margin: '0 6px' }} />

                {/* Year Selector */}
                <div className="d-flex align-items-center flex-shrink-0" title="Select Payroll Year">
                  <i className="fa fa-clock-o text-success me-1" style={{ fontSize: '12px' }} />
                  <select
                    className="form-select form-select-sm border-0 bg-transparent"
                    style={{ 
                      fontSize: '11.5px', 
                      fontWeight: selectedYear ? 600 : 400,
                      color: isDark ? '#f8fafc' : '#1e293b', 
                      cursor: 'pointer',
                      maxWidth: '75px',
                      padding: '0 16px 0 0',
                      outline: 'none',
                      boxShadow: 'none',
                      height: '24px'
                    }}
                    value={selectedYear}
                    onChange={handleYearChange}
                  >
                    <option value="" style={{ color: '#000' }}>-- Year --</option>
                    {YEAR_LIST.map((y) => (
                      <option key={y} value={String(y)} style={{ color: '#000' }}>{y}</option>
                    ))}
                  </select>
                </div>

                {/* Status Indicator */}
                {expiryAlert ? (
                  <>
                    <div style={{ width: '1px', height: '16px', background: isDark ? '#334155' : '#cbd5e1', margin: '0 6px' }} />
                    <span 
                      className={`badge d-inline-flex align-items-center py-1 px-2 flex-shrink-0 ${expiryAlert.isExpired ? 'bg-danger text-white' : 'bg-warning text-dark'}`}
                      style={{ 
                        borderRadius: '12px', 
                        fontSize: '10.5px', 
                        fontWeight: 600,
                        animation: expiryAlert.isExpired ? 'pulse-alert-red 1.8s infinite' : 'pulse-alert-amber 1.8s infinite',
                        cursor: 'pointer',
                        height: '22px'
                      }}
                      title={`Branch: ${expiryAlert.branchName} | Reg No: ${expiryAlert.fbRegNo || 'N/A'} | Expiry: ${expiryAlert.formattedDate}`}
                    >
                      <i className={`fa ${expiryAlert.isExpired ? 'fa-exclamation-triangle' : 'fa-clock-o'} me-1`} />
                      {expiryAlert.isExpired ? `Expired` : `${expiryAlert.diffDays}d left`}
                    </span>
                  </>
                ) : (
                  selectedFirm && selectedMonth && selectedYear && (
                    <>
                      <div style={{ width: '1px', height: '16px', background: isDark ? '#334155' : '#cbd5e1', margin: '0 6px' }} />
                      <span 
                        className="badge d-none d-sm-inline-flex align-items-center py-1 px-2 flex-shrink-0"
                        style={{ 
                          background: isDark ? 'rgba(16, 185, 129, 0.15)' : '#dcfce7',
                          color: isDark ? '#34d399' : '#15803d',
                          border: `1px solid ${isDark ? '#059669' : '#86efac'}`,
                          borderRadius: '12px', 
                          fontSize: '10.5px', 
                          fontWeight: 600,
                          height: '22px'
                        }}
                        title="Active Payroll Session Configured"
                      >
                        <span 
                          style={{ 
                            width: '6px', 
                            height: '6px', 
                            borderRadius: '50%', 
                            background: '#10b981', 
                            display: 'inline-block',
                            marginRight: '4px'
                          }}
                        />
                        Ready
                      </span>
                    </>
                  )
                )}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════
                PART 3: RIGHT (Width = Sidebar Width = 200px)
                Quick Action Bar: Notifications, Shortcuts, Theme, Profile
                ═══════════════════════════════════════════════════════════ */}
            <div className="navbar-part-right">
              {/* Registration Expiry Notification Bell Dropdown */}
              <Dropdown as="div" className="dropdown position-relative">
                <Dropdown.Toggle
                  as="button"
                  className="navbar-action-btn border-0"
                  title={expiryAlert ? `Warning: ${expiryAlert.branchName} Registration is ${expiryAlert.isExpired ? 'Expired' : 'Expiring Soon'}` : 'Notifications'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: expiryAlert ? '#ef4444' : (isDark ? '#cbd5e1' : '#64748b') }}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  {expiryAlert && (
                    <span 
                      className="badge light text-white bg-danger rounded-circle position-absolute" 
                      style={{ top: '4px', right: '4px', width: '8px', height: '8px', padding: 0 }}
                    />
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu align="end" className="mt-2 dropdown-menu dropdown-menu-end p-0 shadow-lg" style={{ minWidth: 'min(320px, calc(100vw - 32px))', borderRadius: '12px', border: `1px solid ${isDark ? '#334155' : 'rgba(0,0,0,0.08)'}` }}>
                  <div className="p-3 border-bottom d-flex align-items-center justify-content-between" style={{ background: expiryAlert ? (expiryAlert.isExpired ? '#fef2f2' : '#fffbeb') : (isDark ? '#1e2638' : '#f8f9fa'), borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <h6 className="mb-0 fw-bold fs-13" style={{ color: expiryAlert ? (expiryAlert.isExpired ? '#991b1b' : '#92400e') : (isDark ? '#fff' : '#333') }}>
                      <i className={`fa ${expiryAlert ? (expiryAlert.isExpired ? 'fa-exclamation-circle text-danger' : 'fa-bell text-warning') : 'fa-bell-o text-muted'} me-2`} />
                      {expiryAlert ? "Registration Expiry Alert" : "System Notifications"}
                    </h6>
                    {expiryAlert && (
                      <span className={`badge ${expiryAlert.isExpired ? 'bg-danger' : 'bg-warning text-dark'}`} style={{ fontSize: '10px' }}>
                        {expiryAlert.isExpired ? 'EXPIRED' : 'EXPIRING SOON'}
                      </span>
                    )}
                  </div>

                  <div className="p-3">
                    {expiryAlert ? (
                      <div>
                        <div className="d-flex align-items-start mb-2">
                          <div 
                            className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                            style={{ 
                              width: '36px', 
                              height: '36px', 
                              background: expiryAlert.isExpired ? '#fee2e2' : '#fef3c7',
                              color: expiryAlert.isExpired ? '#dc2626' : '#d97706',
                              fontSize: '16px' 
                            }}
                          >
                            <i className={`fa ${expiryAlert.isExpired ? 'fa-exclamation-triangle' : 'fa-clock-o'}`} />
                          </div>
                          <div>
                            <strong className="text-dark fs-13 d-block">{expiryAlert.branchName}</strong>
                            <span className="text-muted fs-12 d-block">
                              FB Reg No: <strong>{expiryAlert.fbRegNo || 'N/A'}</strong>
                            </span>
                          </div>
                        </div>

                        <div 
                          className="p-2 rounded mb-2"
                          style={{ 
                            background: expiryAlert.isExpired ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                            border: `1px solid ${expiryAlert.isExpired ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`,
                            fontSize: '12px'
                          }}
                        >
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">Expiry Date:</span>
                            <strong className={expiryAlert.isExpired ? 'text-danger' : 'text-dark'}>{expiryAlert.formattedDate}</strong>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Status:</span>
                            <strong className={expiryAlert.isExpired ? 'text-danger' : 'text-warning'}>
                              {expiryAlert.isExpired ? `Expired (${expiryAlert.diffDays} days ago)` : `Expiring in ${expiryAlert.diffDays} days`}
                            </strong>
                          </div>
                        </div>

                        <p className="text-muted mb-0 fs-11" style={{ lineHeight: '1.4' }}>
                          ⚠️ Please renew factory / establishment registration before the expiry date to avoid compliance penalties.
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-3 text-muted fs-13">
                        <i className="fa fa-check-circle text-success fa-2x mb-2 d-block" />
                        No pending alerts. All active registrations are valid.
                      </div>
                    )}
                  </div>
                </Dropdown.Menu>
              </Dropdown>

              {/* Keyboard Shortcuts Help Button */}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('toggle-keyboard-shortcuts'))}
                className="navbar-action-btn d-none d-md-inline-flex"
                aria-label="Keyboard Shortcuts Help"
                title="Keyboard Shortcuts Help (Alt + K)"
              >
                <i className="fa fa-keyboard" style={{ fontSize: '15px' }} />
              </button>

              {/* Theme Toggle Button */}
              <button
                type="button"
                onClick={toggleTheme}
                className="navbar-action-btn"
                aria-label={background?.value === 'light' ? 'Activate dark mode' : 'Activate light mode'}
                title={background?.value === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
              >
                {background?.value === 'light' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </svg>
                )}
              </button>

              {/* Profile Dropdown */}
              <Dropdown as="div" className="dropdown">
                <Dropdown.Toggle 
                  as="button" 
                  className="navbar-action-btn p-0"
                  style={{ overflow: 'hidden' }}
                  title="Account Settings & Logout"
                >
                  <img 
                    src={profile} 
                    alt="User" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu align="end" className="mt-2 dropdown-menu dropdown-menu-end shadow">
                  <LogoutPage />
                </Dropdown.Menu>
              </Dropdown>
            </div>

          </div>
        </nav>
      </div>

      {/* Embedded CSS for pulsing alert animations */}
      <style>{`
        @keyframes pulse-alert-red {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
        @keyframes pulse-alert-amber {
          0% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
          }
          70% {
            box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default Header;
